import { useRef } from 'react';

/**
 * Thin vertical drag handle that sits between two panels and reports
 * incremental horizontal movement. The parent owns the width state and
 * decides the sign (a left-edge handle subtracts, a right-edge handle adds).
 *
 * Uses pointer capture so the drag keeps tracking even when the cursor
 * outruns the 1px hit area, and widens to a comfortable grab zone on hover.
 */
export function ResizeHandle(props: {
  /** Called with the px delta since the last move event during a drag. */
  onResize: (deltaX: number) => void;
  /** Called once when the drag ends (persist the final width here). */
  onCommit?: () => void;
  ariaLabel: string;
  className?: string;
}) {
  const lastX = useRef<number | null>(null);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={props.ariaLabel}
      className={
        'group relative z-20 -mx-[2px] hidden w-2 shrink-0 cursor-col-resize touch-none select-none md:block ' +
        (props.className ?? '')
      }
      onPointerDown={(e) => {
        e.preventDefault();
        lastX.current = e.clientX;
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (lastX.current === null) return;
        const dx = e.clientX - lastX.current;
        lastX.current = e.clientX;
        if (dx !== 0) props.onResize(dx);
      }}
      onPointerUp={(e) => {
        if (lastX.current === null) return;
        lastX.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
        props.onCommit?.();
      }}
    >
      {/* No permanent line: the panels are already separated by the gap between
          them, and a hairline running the full height read as a seam (owner:
          «полоски между блоками убрать»). A short grip appears only when the
          pointer is on the handle or a drag is in progress. */}
      <span
        aria-hidden
        className="resize-grip pointer-events-none absolute left-1/2 top-1/2 h-10 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/0 transition-colors duration-150 group-hover:bg-accent/55 group-active:bg-accent/80"
      />
    </div>
  );
}
